import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useState } from "react";
import { useSwapOrbitLTerminalMutation } from "Api/Mutations/Merchants/useSwapOrbitLTerminalMutation";
import { useSearchAvailableTerminalsQuery } from "Api/Queries/Merchants/useSearchAvailableTerminalsQuery";
import {
  type TerminalItemDto,
  TerminalInventoryStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding.Detail;

type Props = {
  target: TerminalItemDto;
  merchantPublicID: string;
  onClose: () => void;
};

export const SwapTerminalDialog = ({
  target,
  merchantPublicID,
  onClose,
}: Props) => {
  const { t } = useResource();
  const [searchInput, setSearchInput] = useState("");
  const [selectedSN, setSelectedSN] = useState<string | null>(null);

  // Empty input lists the stock right away, so "no terminals" really means an empty stock, not "start typing".
  const { data: availableTerminals, isLoading: isSearching } =
    useSearchAvailableTerminalsQuery({
      serialNumberPrefix: searchInput,
    });

  const { mutate: swapTerminal, isPending: isSwapping } =
    useSwapOrbitLTerminalMutation({
      onSuccess: (result) => {
        // A P300 pairs by activation code: after the swap the code is free for the replacement device.
        toast.success(
          result.activationCode
            ? t(RESOURCES.SwapTerminalSuccessWithCode, {
                code: result.activationCode,
              })
            : t(RESOURCES.SwapTerminalSuccess),
        );
        onClose();
      },
      onError: () => toast.error(t(RESOURCES.SwapTerminalError)),
    });

  const handleConfirm = () => {
    if (!target.publicID || !selectedSN) {
      return;
    }
    swapTerminal({
      merchantPublicID,
      request: {
        oldTerminalPublicID: target.publicID,
        newSerialNumber: selectedSN,
      },
    });
  };

  return (
    <Dialog
      open
      onClose={isSwapping ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t(RESOURCES.SwapTerminalTitle)}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {target.status === TerminalInventoryStatus.Deployed && (
            <Alert severity="warning">
              {t(RESOURCES.SwapTerminalTmsUpdateWarning)}
            </Alert>
          )}
          <Typography variant="body2">
            {target.serialNumber} ({target.hardwareModel ?? "—"})
          </Typography>
          <Autocomplete
            options={
              availableTerminals?.map(({ serialNumber }) => serialNumber) ?? []
            }
            loading={isSearching}
            // The server already filters; the default client-side filter would hide results while typing.
            filterOptions={(options) => options}
            inputValue={searchInput}
            onInputChange={(_e, value, reason) => {
              setSearchInput(value);
              if (reason !== "reset") {
                setSelectedSN(null);
              }
            }}
            onChange={(_e, value) => setSelectedSN(value)}
            noOptionsText={t(RESOURCES.SwapTerminalNoResults)}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t(RESOURCES.SwapTerminalSearch)}
                size="small"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSwapping}>
          {t(RESOURCES.SwapTerminalCancel)}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSwapping || !selectedSN}
          startIcon={
            isSwapping ? <CircularProgress size={16} /> : <SwapHorizIcon />
          }
        >
          {t(RESOURCES.SwapTerminalConfirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
